import { useEffect, useRef } from 'react';
import { useAtom } from 'jotai';

import { getServerCart } from '@/lib/cart-sync/cart-sync-api';
import { CART_SKIP_CROSS_DEVICE_MERGE_ONCE_KEY } from '@/lib/cart-sync/cart-sync.constants';
import { enrichMergedCartWithFreshPricesAndStock } from '@/lib/cart-sync/enrich-merged-cart-prices-and-stock';
import { mergeLocalAndServerCart } from '@/lib/cart-sync/merge-local-and-server-cart';
import {
  safeSessionStorageGetItem,
  safeSessionStorageRemoveItem,
} from '@/lib/safe-storage';
import { authorizationAtom } from '@/store/authorization-atom';
import { useCart } from '@/store/quick-cart/cart.context';

/**
 * Cross-device cart bootstrap: once per auth mount when logged in, GET /cart and LWW-merge.
 * Does not wait for `/me` — JWT is enough (faster, avoids blocking on profile).
 * Skipped once after login when `runPostLoginCartSync` already pushed a non-empty local cart.
 */
export function CartSyncBootstrap() {
  const [isAuthorized] = useAtom(authorizationAtom);
  const { items, language, hydrateMergedCart, onCartBootstrapComplete } =
    useCart();
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const mergedOnce = useRef(false);

  useEffect(() => {
    if (!isAuthorized) mergedOnce.current = false;
  }, [isAuthorized]);

  useEffect(() => {
    if (!isAuthorized) return;

    if (mergedOnce.current) return;
    mergedOnce.current = true;

    const finish = () => onCartBootstrapComplete();

    try {
      if (safeSessionStorageGetItem(CART_SKIP_CROSS_DEVICE_MERGE_ONCE_KEY)) {
        safeSessionStorageRemoveItem(CART_SKIP_CROSS_DEVICE_MERGE_ONCE_KEY);
        /** Stale key + empty local would skip GET and leave server lines invisible — only skip merge when local actually has lines (post-login push path). */
        if (itemsRef.current.length > 0) {
          finish();
          return;
        }
      }
    } catch {
      /* sessionStorage blocked — continue with GET + merge */
    }

    let cancelled = false;
    (async () => {
      try {
        const { items: serverItems = [] } = await getServerCart();
        if (cancelled) return;
        /** Empty server + non-empty local: merge keeps local lines (fixes race with post-login POST). */
        const merged = await mergeLocalAndServerCart(
          itemsRef.current,
          serverItems,
          language,
        );
        if (cancelled) return;
        const enriched = await enrichMergedCartWithFreshPricesAndStock(merged);
        if (cancelled) return;
        hydrateMergedCart(enriched);
      } catch {
        /* 404 / network — no user-visible error */
      } finally {
        if (!cancelled) finish();
      }
    })();

    return () => {
      // Cancel any in-flight merge so we don't apply server state to an unmounted/
      // logged-out provider. Do NOT reset `mergedOnce` here: that would let the
      // effect re-fire (and re-fetch /cart) whenever a context dep reference
      // changes (e.g. language). Logout is the only legitimate re-entry trigger,
      // and that's handled by the dedicated `[isAuthorized]` effect above.
      cancelled = true;
    };
  }, [isAuthorized, language, hydrateMergedCart, onCartBootstrapComplete]);

  return null;
}
